/**
 * Opportunities API
 * Query and manage opportunities using Trace API entities system
 * Entity types: 'rfp_opportunity' and 'rfp_award'
 */

import { queryEntities, createEntity, Entity, getAPIClient } from '@tracekit/core';
import { Opportunity, Award } from '../services/dataSourceIntegrations';

export interface RecommendedKeyword {
  keyword: string;
  explanation: string;
  citations: Array<{
    id?: number;
    source?: string;
    artifact_name?: string;
    artifact_filename?: string;
    artifact_id?: string;
    chunk_id?: string;
    page?: number;
    section?: string;
    content?: string;
    confidence?: number;
    relevance_score?: number;
  }>;
  citation_count: number;
}

export interface RecommendedKeywordsResponse {
  keywords: RecommendedKeyword[];
  total_count: number;
  task_id?: string;
  cached?: boolean;
  // Location and Department Filters (NEW)
  location_keywords?: string[];
  department_names?: string[];
  location_count?: number;
  department_count?: number;
}

/**
 * Opportunity entity structure stored in Trace API
 * Entity type: 'rfp_opportunity' or 'rfp_award'
 */
export interface OpportunityEntity extends Entity {
  entity_type: 'rfp_opportunity' | 'rfp_award';
  data: {
    opportunity_id: string;
    source: 'canada_grants' | 'canada_tenders' | 'samgov' | 'worldbank';
    title: string;
    description: string;
    organization: string;
    deadline: string;
    value?: string;
    url: string;
    document_url?: string;
    keywords: string[];
    service_lines: string[];
    status?: 'open' | 'closed' | 'awarded' | 'cancelled';
    saved?: boolean;
    proposal_id?: string; // Link to generated proposal report
    created_at: string;
    updated_at: string;
    
    // RFP Structure V3 (added 2026-01-24)
    rfp_structure?: import('../types/rfp').RfpStructureMetadata;
  };
  
  // RFP Extraction Status Fields (added 2026-01-24)
  rfp_structure_metadata?: import('../types/rfp').RfpStructureMetadata;
  rfp_extraction_status?: import('../types/rfp').RfpExtractionStatus;
  rfp_extraction_method?: import('../types/rfp').RfpExtractionMethod;
  rfp_extraction_quality_score?: number; // 0-100
  rfp_extracted_at?: string;
  rfp_extraction_error?: string;
}

/**
 * Query opportunities using Trace API queryEntities
 * Entity type: 'rfp_opportunity'
 */
export async function queryOpportunities(
  projectId: string,
  filters?: {
    source?: string;
    status?: string;
    service_line?: string;
    saved?: boolean;
    search?: string;
  },
  limit: number = 50,
  offset: number = 0
): Promise<{ opportunities: OpportunityEntity[]; total_count: number }> {
  // Build filters for Trace API entity query
  const entityFilters: Record<string, any> = {};

  // Trace API filters use dot notation for nested JSONB fields
  if (filters?.source) {
    entityFilters['data.source'] = filters.source;
  }
  if (filters?.status) {
    entityFilters['data.status'] = filters.status;
  }
  if (filters?.service_line) {
    // For array contains, use the filter format supported by Trace API
    entityFilters['data.service_lines'] = filters.service_line;
  }
  if (filters?.saved !== undefined) {
    entityFilters['data.saved'] = filters.saved;
  }

  // Query using existing Trace API entity system
  const response = await queryEntities({
    project_id: projectId,
    entity_type: 'rfp_opportunity',
    filters: entityFilters,
    limit,
    offset,
    sort_by: 'created_at',
    order: 'desc',
  });

  let opportunities = response.entities as OpportunityEntity[];

  // Client-side search filtering (could be moved to server-side with text search)
  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    opportunities = opportunities.filter(
      (opp) =>
        opp.data?.title?.toLowerCase().includes(searchLower) ||
        opp.data?.description?.toLowerCase().includes(searchLower) ||
        opp.data?.organization?.toLowerCase().includes(searchLower) ||
        opp.data?.keywords?.some((k: string) => k.toLowerCase().includes(searchLower))
    );
  }

  return {
    opportunities,
    total_count: response.total_count || opportunities.length,
  };
}

/**
 * Query awards using Trace API queryEntities
 * Entity type: 'rfp_award'
 */
export async function queryAwards(
  projectId: string,
  filters?: {
    source?: string;
    service_line?: string;
    year?: number;
  },
  limit: number = 50,
  offset: number = 0
): Promise<{ awards: OpportunityEntity[]; total_count: number }> {
  const entityFilters: Record<string, any> = {};

  if (filters?.source) {
    entityFilters['data.source'] = filters.source;
  }
  if (filters?.service_line) {
    entityFilters['data.service_lines'] = filters.service_line;
  }
  if (filters?.year) {
    // Filter by year in deadline
    entityFilters['data.deadline_gte'] = `${filters.year}-01-01`;
    entityFilters['data.deadline_lte'] = `${filters.year}-12-31`;
  }

  const response = await queryEntities({
    project_id: projectId,
    entity_type: 'rfp_award',
    filters: entityFilters,
    limit,
    offset,
    sort_by: 'created_at',
    order: 'desc',
  });

  return {
    awards: response.entities as OpportunityEntity[],
    total_count: response.total_count || 0,
  };
}

/**
 * Save an opportunity (update saved status)
 * Creates a new entity with updated saved flag
 */
export async function saveOpportunity(
  projectId: string,
  opportunityId: string
): Promise<OpportunityEntity> {
  // Query to find the opportunity
  const { opportunities } = await queryOpportunities(projectId, {}, 1000, 0);
  const opportunity = opportunities.find((opp) => opp.data?.opportunity_id === opportunityId);

  if (!opportunity) {
    throw new Error('Opportunity not found');
  }

  // Create new entity with updated saved status
  const updatedData = {
    ...opportunity.data,
    saved: true,
    updated_at: new Date().toISOString(),
  };

  const updatedEntity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_opportunity',
    data: updatedData,
    source_artifact_id: opportunity.source_artifact_id || undefined,
  });

  return updatedEntity as OpportunityEntity;
}

/**
 * Create opportunity entity from external opportunity data
 * Stores as 'rfp_opportunity' entity type in Trace API
 */
export async function createOpportunityEntity(
  projectId: string,
  opportunity: Opportunity
): Promise<OpportunityEntity> {
  const entity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_opportunity',
    data: {
      opportunity_id: opportunity.id,
      source: opportunity.source,
      title: opportunity.title,
      description: opportunity.description,
      organization: opportunity.organization,
      deadline: opportunity.deadline,
      value: opportunity.value,
      url: opportunity.url,
      document_url: opportunity.document_url,
      keywords: opportunity.keywords,
      service_lines: opportunity.service_lines,
      status: 'open',
      saved: false,
      created_at: opportunity.created_at,
      updated_at: opportunity.updated_at,
    },
  });

  return entity as OpportunityEntity;
}

/**
 * Mark opportunity as awarded (convert to award entity)
 */
export async function markOpportunityAsAwarded(
  projectId: string,
  opportunityId: string,
  proposalId?: string
): Promise<OpportunityEntity> {
  const { opportunities } = await queryOpportunities(projectId, {}, 1000, 0);
  const opportunity = opportunities.find((opp) => opp.data?.opportunity_id === opportunityId);

  if (!opportunity) {
    throw new Error('Opportunity not found');
  }

  // Create award entity
  const awardData = {
    ...opportunity.data,
    status: 'awarded',
    proposal_id: proposalId,
    updated_at: new Date().toISOString(),
  };

  const awardEntity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_award',
    data: awardData,
    source_artifact_id: opportunity.source_artifact_id || undefined,
  });

  return awardEntity as OpportunityEntity;
}

/**
 * Create award entity from external award data
 * Stores as 'rfp_award' entity type in Trace API
 */
export async function createAwardEntity(
  projectId: string,
  award: Award
): Promise<OpportunityEntity> {
  const entity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_award',
    data: {
      award_id: award.id,
      opportunity_id: award.opportunity_id,
      source: award.source,
      title: award.title,
      description: award.description || '',
      organization: award.organization,
      awardee: award.awardee,
      award_date: award.award_date,
      award_value: award.award_value,
      award_number: award.award_number,
      url: award.url,
      created_at: award.created_at,
      updated_at: award.updated_at,
    },
  });

  return entity as OpportunityEntity;
}

/**
 * Get recommended search keywords for opportunities based on company capabilities
 * Uses RAG to analyze company capabilities and generate effective search keywords
 * 
 * NEW ASYNC BEHAVIOR:
 * - Returns immediately if cached
 * - Otherwise returns task_id for polling status
 */
export async function getRecommendedKeywords(
  projectId: string,
  bypassCache: boolean = false
): Promise<RecommendedKeywordsResponse & { task_id?: string; cached?: boolean }> {
  const client = getAPIClient();
  const response = await client.post<RecommendedKeywordsResponse & { task_id?: string; cached?: boolean }>(
    '/api/opportunities/recommended-keywords',
    {},
    {
      params: {
        project_id: projectId,
        bypass_cache: bypassCache,
      },
    }
  );
  return response;
}

/**
 * Get status of keyword generation task
 */
export interface KeywordTaskStatus {
  status: 'running' | 'completed' | 'failed';
  stage: string;
  message: string;
  progress: number;
  keywords?: RecommendedKeyword[];
  total_count?: number;
  // Location and Department Filters (NEW)
  location_keywords?: string[];
  department_names?: string[];
  location_count?: number;
  department_count?: number;
  error?: string;
}

export async function getKeywordTaskStatus(
  taskId: string
): Promise<KeywordTaskStatus> {
  const client = getAPIClient();
  const response = await client.get<KeywordTaskStatus>(
    `/api/opportunities/recommended-keywords/status/${taskId}`
  );
  return response;
}

/**
 * Stream recommended keywords with real-time progress updates
 * Uses Server-Sent Events (SSE) for progressive updates
 */
export interface KeywordStreamCallbacks {
  onStatus?: (data: {
    stage: string;
    message: string;
    elapsed?: number;
    opportunities_count?: number;
    common_terms_count?: number;
  }) => void;
  onKeywords?: (keywords: RecommendedKeyword[]) => void;
  onComplete?: (data: {
    keywords: RecommendedKeyword[];
    total_count: number;
    elapsed: number;
    opportunities_analyzed?: number;
    common_terms_found?: number;
    // Location and Department Filters (NEW)
    location_keywords?: string[];
    department_names?: string[];
    location_count?: number;
    department_count?: number;
    cached?: boolean;
  }) => void;
  onError?: (error: Error) => void;
}

export async function streamRecommendedKeywords(
  projectId: string,
  bypassCache: boolean = false,
  callbacks: KeywordStreamCallbacks
): Promise<() => void> {
  const { getAPIBaseUrl, getAuthHeaders } = await import('@tracekit/core');
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();
  
  let aborted = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  
  const abort = () => {
    aborted = true;
    if (reader) {
      reader.cancel();
      reader = null;
    }
  };
  
  // Start streaming in background
  (async () => {
    try {
      const url = `${baseUrl}/api/opportunities/recommended-keywords/stream?project_id=${encodeURIComponent(projectId)}&bypass_cache=${bypassCache}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
        body: JSON.stringify({}),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      
      if (!reader) {
        throw new Error('Response body is not readable');
      }
      
      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.trim() === '') continue; // Skip empty lines
          
          if (line.startsWith('event: ')) {
            currentEventType = line.substring(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              if (currentEventType === 'status') {
                callbacks.onStatus?.(data);
              } else if (currentEventType === 'keywords') {
                callbacks.onKeywords?.(data.keywords || []);
              } else if (currentEventType === 'complete') {
                // Log location/department filter data if present
                if (data.location_keywords || data.department_names) {
                  console.log(
                    `[Keywords] Received ${data.location_count || 0} locations, ${data.department_count || 0} departments`
                  );
                }
                callbacks.onComplete?.(data);
                abort();
                break;
              } else if (currentEventType === 'error') {
                callbacks.onError?.(new Error(data.message || 'Unknown error occurred'));
                abort();
                break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line);
            }
            currentEventType = ''; // Reset after processing
          }
        }
      }
    } catch (error: any) {
      if (!aborted) {
        callbacks.onError?.(new Error(error?.message || 'Stream error occurred'));
      }
    } finally {
      if (reader) {
        reader.releaseLock();
        reader = null;
      }
    }
  })();
  
  return abort;
}

/**
 * Stream RFP document processing job progress
 * Uses Server-Sent Events (SSE) for real-time updates
 */
export interface RFPJobStreamCallbacks {
  onStatus?: (data: {
    stage: string;
    message: string;
    progress: number;
    job_status: string;
    job_type: string;
    elapsed: number;
  }) => void;
  onComplete?: (data: {
    success: boolean;
    message: string;
    elapsed: number;
    job_id: string;
  }) => void;
  onError?: (error: Error) => void;
}

export async function streamRFPJobProgress(
  opportunityId: string,
  jobId: string,
  projectId: string,
  callbacks: RFPJobStreamCallbacks
): Promise<() => void> {
  const { getAPIBaseUrl, getAuthHeaders } = await import('@tracekit/core');
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();
  
  let aborted = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  
  const abort = () => {
    aborted = true;
    if (reader) {
      reader.cancel();
      reader = null;
    }
  };
  
  // Start streaming in background
  (async () => {
    try {
      const url = `${baseUrl}/api/opportunities/${opportunityId}/rfp-documents/jobs/${jobId}/stream?project_id=${encodeURIComponent(projectId)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      if (!response.body) {
        throw new Error('No response body received');
      }
      
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      
      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.trim() === '') continue;
          
          if (line.startsWith('event: ')) {
            currentEventType = line.substring(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              if (currentEventType === 'status') {
                // Log all status updates for debugging
                console.log('[RFP Job Stream] Status update:', data);
                callbacks.onStatus?.(data);
              } else if (currentEventType === 'complete') {
                callbacks.onComplete?.(data);
                abort();
                break;
              } else if (currentEventType === 'error') {
                callbacks.onError?.(new Error(data.message || 'Unknown error occurred'));
                abort();
                break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line);
            }
            currentEventType = '';
          }
        }
      }
    } catch (error: any) {
      if (!aborted) {
        callbacks.onError?.(new Error(error?.message || 'Stream error occurred'));
      }
    } finally {
      if (reader) {
        reader.releaseLock();
        reader = null;
      }
    }
  })();
  
  return abort;
}

/**
 * V3 RFP Structure Extraction API Methods
 */

/**
 * Trigger V3 RFP structure extraction for an opportunity
 * @param opportunityId - Opportunity UUID
 * @param projectId - Project UUID
 * @param artifactIds - Optional list of artifact IDs to extract from
 * @param forceReExtract - Force re-extraction even if recent extraction exists
 */
export async function extractOpportunityStructureV3(
  opportunityId: string,
  projectId: string,
  artifactIds?: string[],
  forceReExtract: boolean = false
): Promise<{
  status: string;
  extraction_id: string;
  opportunity_id: string;
  quality_score: number;
  extraction_method: string;
  message: string;
}> {
  const client = getAPIClient();
  const response = await client.post(
    `/api/rfp/opportunities/${opportunityId}/extract`,
    {
      artifact_ids: artifactIds,
      force_re_extract: forceReExtract,
    },
    {
      params: { project_id: projectId },
    }
  );
  
  if (!response || !response.data) {
    console.error('[extractOpportunityStructureV3] Empty response from API:', response);
    throw new Error('Empty response from extraction API');
  }
  
  return response.data;
}

/**
 * Get the extracted RFP structure for an opportunity
 * @param opportunityId - Opportunity UUID
 * @param projectId - Project UUID
 */
export async function getOpportunityStructureV3(
  opportunityId: string,
  projectId: string
): Promise<{
  opportunity_id: string;
  structure: import('../types/rfp').RfpStructureMetadata | null;
  extraction_status: import('../types/rfp').RfpExtractionStatus;
  quality_score: number | null;
  extracted_at: string | null;
}> {
  const client = getAPIClient();
  const response = await client.get(
    `/api/rfp/opportunities/${opportunityId}/structure`,
    {
      params: { project_id: projectId },
    }
  );
  
  if (!response || !response.data) {
    console.error('[getOpportunityStructureV3] Empty response from API:', response);
    throw new Error('Empty response from structure API');
  }
  
  return response.data;
}

/**
 * Get the extraction status for an opportunity
 * @param opportunityId - Opportunity UUID
 * @param projectId - Project UUID
 */
export async function getOpportunityExtractionStatusV3(
  opportunityId: string,
  projectId: string
): Promise<{
  opportunity_id: string;
  extraction_status: import('../types/rfp').RfpExtractionStatus;
  extraction_id?: string;
  extraction_method: string | null;
  quality_score: number | null;
  extracted_at: string | null;
  error_message: string | null;
  can_re_extract: boolean;
  next_extraction_available_at: string | null;
  sections_count?: number;
  requirements_count?: number;
  criteria_count?: number;
  structure?: any; // Full structure data from Redis
}> {
  const client = getAPIClient();
  const response = await client.get(
    `/api/rfp/opportunities/${opportunityId}/extraction-status`,
    {
      params: { project_id: projectId },
    }
  );
  
  // Response validation: Check if response exists and has data
  if (!response) {
    console.error('[getOpportunityExtractionStatusV3] No response from API');
    throw new Error('No response from extraction status API');
  }
  
  // API client might return data directly or wrapped in .data
  const data = response.data || response;
  
  if (!data || typeof data !== 'object') {
    console.error('[getOpportunityExtractionStatusV3] Invalid response data:', data);
    throw new Error('Invalid response from extraction status API');
  }
  
  // Log for debugging
  console.log('[getOpportunityExtractionStatusV3] ✅ Status received:', {
    extraction_id: data.extraction_id,
    status: data.status || data.extraction_status,
    sections: data.sections_count,
    has_structure: !!data.structure
  });
  
  return data;
}

/**
 * Generate proposal template from approved RFP structure
 * Phase 2: Uses extracted structure + artifact chunks to generate comprehensive template with questions
 */
export async function generateTemplateFromRFPStructure(
  opportunityId: string,
  projectId: string,
  artifactIds: string[]
): Promise<any> {
  const client = getAPIClient();
  console.log('[generateTemplateFromRFPStructure] Starting Phase 2 template generation:', {
    opportunityId,
    artifactIds: artifactIds.length
  });
  
  const response = await client.post(
    `/api/opportunities/${opportunityId}/proposal-template/from-rfp`,
    {
      artifact_ids: artifactIds,
      adapt_questions: true
    },
    {
      params: { project_id: projectId },
      timeout: 120000 // 2 minutes for comprehensive generation
    }
  );
  
  if (!response || !response.data) {
    console.error('[generateTemplateFromRFPStructure] Invalid response');
    throw new Error('Invalid response from template generation API');
  }
  
  console.log('[generateTemplateFromRFPStructure] ✅ Template generated:', {
    sections: response.data.sections?.length || 0,
    questions: response.data.default_questions?.length || 0,
    source: response.data.template_source
  });
  
  return response.data;
}